/* eslint-disable max-len */
import { DASHBOARD_STATUS_COLOR_MAP } from '../constants';

const STATUS_COLORS = DASHBOARD_STATUS_COLOR_MAP;
const IN_TRANSIT_COLORS = STATUS_COLORS['In Transit'];
const DELIVERED_COLORS = STATUS_COLORS.Delivered;
const IN_PROCESS_COLORS = STATUS_COLORS['In Process'];
const CANCELLED_COLORS = STATUS_COLORS.Cancelled;

const gridView1DetailsStyles = () => ({
    // ========== MAIN CONTAINER ==========
    mainContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#F7F8FA',
        overflow: 'hidden',
    },

    // Main drawer (when not expanded)
    mainDrawer: {
        '& .ant-drawer-body': {
            padding: 0,
            background: '#F7F8FA',
            height: '100%',
            overflow: 'hidden',
        },
        '& .ant-drawer-header': {
            padding: 0,
            border: 'none',
            background: '#F7F8FA',
        },
        '& .ant-drawer-title': {
            width: '100%',
        },
    },

    // ========== TOP SECTION ==========
    topSection: {
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px',
        background: '#F7F8FA',
    },

    // Left side - back arrow, reference, status
    topLeftSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
    },

    // Back arrow button
    backButton: {
        width: 24,
        height: 24,
        cursor: 'pointer',
        fontSize: 18,
        color: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            color: '#165ACE',
        },
    },

    // Customer reference number
    customerReference: {
        color: 'black',
        fontSize: 28,
        fontFamily: 'Open Sans',
        fontWeight: 600,
        wordWrap: 'break-word',
    },

    // Expand icon (shown in drawer mode)
    expandIcon: {
        cursor: 'pointer',
        fontSize: 18,
        color: '#165ACE',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            color: '#40A9FF',
        },
    },

    // Status tag with icon
    statusTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        borderRadius: 28,
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 600,
    },

    // Status tag variants
    statusTagInTransit: {
        backgroundColor: IN_TRANSIT_COLORS.backgroundColor,
        color: IN_TRANSIT_COLORS.textColor,
    },

    statusTagDelivered: {
        backgroundColor: DELIVERED_COLORS.backgroundColor,
        color: DELIVERED_COLORS.textColor,
    },

    statusTagInProcess: {
        backgroundColor: IN_PROCESS_COLORS.backgroundColor,
        color: IN_PROCESS_COLORS.textColor,
    },

    statusTagCancelled: {
        backgroundColor: CANCELLED_COLORS.backgroundColor,
        color: CANCELLED_COLORS.textColor,
    },

    // Right side - gear icon and print button
    topRightSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },

    // Gear icon button
    gearButton: {
        width: 40,
        height: 40,
        padding: '18px 24px',
        borderRadius: 8,
        border: '1.5px solid #E6EAF0',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
            borderColor: '#165ACE',
        },
        '& .anticon': {
            fontSize: 18,
            color: '#1C1B1F',
        },
    },

    // Print label button
    printButton: {
        height: 40,
        padding: '18px 24px',
        borderRadius: 8,
        border: '1.5px solid #E6EAF0',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
            borderColor: '#165ACE',
        },
        '& .anticon': {
            fontSize: 15,
            color: '#1C1B1F',
        },
    },

    printButtonText: {
        color: 'black',
        fontSize: 14,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    // ========== CONTENT SECTION (Two Columns) ==========
    contentSection: {
        flex: 1,
        padding: '0 32px 32px 32px',
        overflow: 'auto',
        height: '100%',
    },

    contentRow: {
        height: '100%',
    },

    // Left column (Timeline)
    leftColumn: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },

    // Right column (Details)
    rightColumn: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },

    // ========== TIMELINE CARD ==========
    timelineCard: {
        background: 'white',
        borderRadius: 10,
        border: '1.5px solid #F0F2F6',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        height: '100%',
        overflow: 'auto',
    },

    // Timeline header
    timelineHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },

    // Timeline title
    timelineTitle: {
        color: 'black',
        fontSize: 20,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    // Carrier logo container
    carrierLogoContainer: {
        width: 103,
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        overflow: 'hidden',
    },

    carrierLogoImage: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
    },

    // Timeline content
    timelineContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
    },

    // ========== TIMELINE ITEM ==========
    timelineItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 28,
    },

    // Plane icon (20x20 gray square placeholder)
    planeIcon: {
        width: 20,
        height: 20,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    planeIconImage: {
        width: '100%',
        height: '100%',
    },

    // Status icon (checkmark or circle)
    statusIcon: {
        width: 16.67,
        height: 16.67,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .anticon': {
            fontSize: 16.67,
        },
    },

    statusIconAchieved: {
        color: '#33C658',
    },

    statusIconCurrent: {
        color: '#0052CC',
    },

    statusIconPending: {
        color: '#E5E6EA',
    },

    // Timeline item content
    timelineItemContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },

    // Status name (In Process, Packed, etc.)
    statusName: {
        color: 'black',
        fontSize: 18,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    statusNamePending: {
        color: 'black',
        opacity: 0.5,
        fontSize: 18,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    // Event details
    eventDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },

    eventName: {
        color: 'rgba(0, 0, 0, 0.40)',
        fontSize: 16,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    eventDateTime: {
        color: 'rgba(0, 0, 0, 0.40)',
        fontSize: 12,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    // ========== INFO CARDS (Protocol ID / AWB) ==========
    infoCard: {
        background: 'white',
        borderRadius: 10,
        border: '1.5px solid #F0F2F6',
        padding: '18px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },

    infoLabel: {
        color: 'rgba(0, 0, 0, 0.50)',
        fontSize: 14,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    infoValue: {
        color: 'black',
        fontSize: 18,
        fontFamily: 'Open Sans',
        fontWeight: 600,
    },

    // ========== DETAILS CARD (Tabs Section) ==========
    detailsCard: {
        background: 'white',
        borderRadius: 10,
        border: '1.5px solid #F0F2F6',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'auto',
    },

    // ========== LOADING STATE ==========
    loadingContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F7F8FA',
    },

    // ========== RESPONSIVE BEHAVIOR ==========
    '@media (max-width: 1200px)': {
        topSection: {
            padding: '20px 24px',
        },
        contentSection: {
            padding: '0 24px 24px 24px',
        },
        customerReference: {
            fontSize: 24,
        },
    },

    '@media (max-width: 768px)': {
        topSection: {
            padding: '16px 20px',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 16,
        },
        topLeftSection: {
            flexWrap: 'wrap',
        },
        topRightSection: {
            justifyContent: 'flex-end',
        },
        contentSection: {
            padding: '0 20px 20px 20px',
        },
        customerReference: {
            fontSize: 20,
        },
        rightColumn: {
            gap: 12,
        },
        timelineCard: {
            padding: '20px 16px',
            gap: 20,
        },
        detailsCard: {
            padding: '24px 20px',
        },
    },

    '@media (max-width: 480px)': {
        topSection: {
            padding: '12px 16px',
            gap: 12,
        },
        contentSection: {
            padding: '0 16px 16px 16px',
        },
        customerReference: {
            fontSize: 18,
        },
        statusTag: {
            fontSize: 12,
            padding: '4px 10px',
        },
        timelineCard: {
            padding: '16px 12px',
            gap: 16,
        },
        timelineTitle: {
            fontSize: 18,
        },
        carrierLogoContainer: {
            width: 80,
            height: 50,
            padding: '8px 20px',
        },
        statusName: {
            fontSize: 16,
        },
        statusNamePending: {
            fontSize: 16,
        },
        eventName: {
            fontSize: 14,
        },
        infoCard: {
            padding: '14px 20px',
        },
        infoLabel: {
            fontSize: 12,
        },
        infoValue: {
            fontSize: 16,
        },
        detailsCard: {
            padding: '20px 16px',
        },
    },
});

export {
    gridView1DetailsStyles,
};
